import * as React from "react";
import * as ReactDOM from "react-dom";
import styles from "./Modal.module.scss";
import {PropsWithChildren} from "react";
import {useTranslation} from "react-i18next";

// create a portal to an Element to render the cart in
const CartPortal: React.FC<{children?: React.ReactNode}> = (props) => {
  return ReactDOM.createPortal(props.children, document.getElementById("ModalRootElement") as HTMLDivElement);
};

type ModalProps = {
  closeFunction?: () => void;
  fullScreen?: boolean;
  isVisible: boolean;
  modalTitle?: string | HTMLElement;
  customClass?: string;
};

const Modal: React.FC<PropsWithChildren<ModalProps>> = (props) => {
  const {children, closeFunction, fullScreen, isVisible, customClass} = props;
  const t = useTranslation().t;
  const modalRef = React.useRef<HTMLElement>(null);
  const [previousFocusedElement, setPreviousFocusedElement] = React.useState<HTMLElement | null>(null);
  const closeButtonId = React.useId();
  const dialogWindowClass = fullScreen
    ? `${styles.popupFullScreen} full-screen-cart`
    : `${styles.popupSmall} popup-screen-cart`;

  const closeModal = () => {
    if (closeFunction) {
      closeFunction();
    }
  };

  const closeOnEsc = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeModal();
    }
  };

  const onClickProxy = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    closeModal();
  };

  React.useEffect(() => {
    const anyModalSection = document.querySelector("#ModalRootElement > section");
    const pageContent = document.querySelector("body > div.w");

    if (isVisible) {
      setPreviousFocusedElement(document.activeElement as HTMLElement);
      focusCloseButton();
    }

    if (props.modalTitle) {
      if (typeof props.modalTitle === "string") {
        modalRef.current?.setAttribute("aria-label", props.modalTitle);
      } else if (props.modalTitle.getAttribute("id")) {
        modalRef.current?.setAttribute("aria-labelledby", props.modalTitle.getAttribute("id") || "");
      }
    }

    document.body.style.overflow = !!anyModalSection ? "hidden" : "";
    if (anyModalSection) {
      pageContent?.setAttribute("inert", "true");
      modalRef.current?.addEventListener("keydown", closeOnEsc);
    } else {
      pageContent?.removeAttribute("inert");
      modalRef.current?.removeEventListener("keydown", closeOnEsc);
      previousFocusedElement?.focus();
    }
  });

  if (!isVisible) {
    return;
  }

  const focusCloseButton = () => {
    const closeButton = document.getElementById(closeButtonId);
    if (closeButton) {
      closeButton.focus();
    } else {
      // focus first child element of modal
      (modalRef.current?.children[0] as HTMLElement).focus();
    }
  };

  return (
    <CartPortal>
      {!fullScreen && <div className={styles.backdrop} onClick={onClickProxy} aria-hidden="true" />}
      <section
        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        className={[dialogWindowClass, customClass].join(" ")}
        role="dialog"
        aria-modal="true"
        ref={modalRef}
      >
        {closeFunction && (
          <button
            id={closeButtonId}
            className={`icon-close ${styles.closeButton}`}
            onClick={onClickProxy}
            aria-label={t("general.closeModal")}
          />
        )}
        {children}

        {
          // Hidden element which resets focus to top of modal when focussed
          !fullScreen && <div className="visually-hidden" onFocus={focusCloseButton} tabIndex={0}></div>
        }
      </section>
    </CartPortal>
  );
};

export default Modal;
